import { inject, injectable } from 'inversify';

import { useConfigStore } from '../stores/ConfigStore';
import type { GetMediaParams } from '../../types/media';
import { getNamedModule } from '../modules/container';
import ContentApiService from '../services/ContentApiService';
import type { PlaylistItem } from '../../types/playlist';
import EntitlementService from '../services/EntitlementService';
import { ENTITLEMENT_TYPE } from '../modules/types';
import { logError } from '../logger';
import { useAccountStore } from '../stores/AccountStore';
import { isLocked } from '../utils/entitlements';

import AccountController from './AccountController';
import AccessController from './AccessController';
import CheckoutController from './CheckoutController';

@injectable()
export default class EntitlementController {
  private accountController: AccountController;
  private accessController: AccessController;
  private checkoutController: CheckoutController;
  private contentApiService: ContentApiService;
  private entitlementService: EntitlementService | undefined;

  constructor(
    @inject(ENTITLEMENT_TYPE) entitlementType: string | null,
    @inject(AccountController) accountController: AccountController,
    @inject(AccessController) accessController: AccessController,
    @inject(CheckoutController) checkoutController: CheckoutController,
    @inject(ContentApiService) contentApiService: ContentApiService,
  ) {
    this.accountController = accountController;
    this.accessController = accessController;
    this.checkoutController = checkoutController;
    this.contentApiService = contentApiService;
    this.entitlementService = getNamedModule(EntitlementService, entitlementType, false);
  }

  protected validateGeoRestriction = async (media?: PlaylistItem) => {
    const m3u8 = media?.sources.find((source) => source.file.indexOf('.m3u8') !== -1);

    if (m3u8) {
      const response = await fetch(m3u8.file, { method: 'HEAD' });
      if (response.status === 403) {
        throw new Error('access blocked');
      }
    }
  };

  getSignedMedia = async (id: string, language?: string, params?: GetMediaParams, isPublic?: boolean) => {
    const { config, settings, accessModel } = useConfigStore.getState();
    const isAccessBridgeEnabled = !!settings?.apiAccessBridgeUrl;
    const accessType = isPublic ? 'public' : accessModel.toLowerCase(); // Can be: public | avod | authvod | svod

    let signedMediaItem;

    try {
      // signing is handled by access bridge
      if (isAccessBridgeEnabled) {
        signedMediaItem = await this.accessController.getMediaById(id, language);
      } else {
        const authData = await this.accountController.getAuthData();
        const token = await this.entitlementService?.getMediaToken(config, id, authData?.jwt, params, accessType);

        signedMediaItem = await this.contentApiService.getMediaById({ id, token, language, config });
      }

      await this.validateGeoRestriction(signedMediaItem);

      return signedMediaItem;
    } catch (error: unknown) {
      logError('EntitlementController', 'Failed to obtain the signed media item', { error });
      throw error;
    }
  };

  checkEntitlement = async (playlistItem: PlaylistItem) => {
    const { accessModel } = useConfigStore.getState();
    const { user, subscription } = useAccountStore.getState();

    const isPreEntitled = playlistItem && !isLocked(accessModel, !!user, !!subscription, playlistItem);
    const mediaOffers = playlistItem?.mediaOffers || [];

    const mediaEntitlements = await Promise.all(
      mediaOffers.map(async ({ id }) => {
        if (!playlistItem || !user || !user.id || !id || isPreEntitled) return null;

        const isEntitled = await this.checkoutController.getEntitlements({ offerId: id });

        return isEntitled;
      }),
    );

    const isMediaEntitled = !!user && mediaEntitlements.some((item) => item?.responseData?.accessGranted);

    return !!playlistItem && (isPreEntitled || isMediaEntitled);
  };
}
